import { getUiLocale } from '@/i18n/locale';
import { t, useUiLocale } from '@/i18n/locale';
import {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { Mic, Volume2, Square } from 'lucide-react';
import { ApiError } from '@/lib/api';
import { useCompanion } from './CompanionContext';
import { recordingToWav } from './companionRecording';
import type { BuddyMessage, BuddyVoiceSettings } from './companionApi';
function voiceError(error: unknown) {
  const code = error instanceof ApiError ? error.code : '';
  if (code === 'COMPANION_VOICE_DAILY_LIMIT')
    return t("Today's voice time is used up. We can still chat with words.");
  if (code === 'COMPANION_VOICE_PERMISSION_REQUIRED')
    return t('Ask a grown-up to turn this voice feature on.');
  if (code === 'COMPANION_VOICE_CHANGED' || code === 'COMPANION_CONTEXT_CHANGED')
    return t('Your settings changed. Refresh and try again.');
  if (code === 'COMPANION_VOICE_REQUEST_UNCERTAIN')
    return t('That voice request could not be confirmed. Please use text for now.');
  if (code === 'COMPANION_VOICE_REQUEST_PENDING')
    return t('This request is still being checked. Please wait before trying again.');
  return t('Audio is unavailable right now.');
}
interface AudioSession {
  settings: BuddyVoiceSettings | null;
  loading: boolean;
  playing: boolean;
  active: string | null;
  busy: boolean;
  error: string;
  volume: number;
  speed: number;
  setVolume: (n: number) => void;
  setSpeed: (n: number) => void;
  play: (token?: string, sample?: boolean) => Promise<void>;
  stop: () => void;
  refresh: () => Promise<void>;
}
const AudioSessionContext = createContext<AudioSession | null>(null);
function useBuddyAudio() {
  const value = useContext(AudioSessionContext);
  if (!value) throw Error('Audio session missing');
  return value;
}
export function CompanionAudioProvider({ children }: { children: ReactNode }) {
  const locale = useUiLocale();
  const session = useCompanion(),
    location = useLocation(),
    key = ['companion-voice', session.audience, session.kidId];
  const query = useQuery({
    queryKey: key,
    queryFn: () => session.client.voice(session.kidId, session.audience === 'parent'),
    enabled: !!session.snapshot,
    retry: false,
    refetchInterval: 15000,
    refetchOnWindowFocus: 'always',
  });
  const [audioState, setAudioState] = useState({ playing: false, busy: false, error: '' }),
    [volume, setVolume] = useState(0.8),
    [speed, setSpeed] = useState(1),
    [active, setActive] = useState<string | null>(null);
  const audio = useRef<HTMLAudioElement | null>(null),
    url = useRef<string | null>(null),
    abort = useRef<AbortController | null>(null),
    epoch = useRef(0),
    cache = useRef(new Map<string, { audio_base64: string; mime_type: string }>()),
    requests = useRef(new Map<string, string>());
  const stop = useCallback(() => {
    epoch.current++;
    setActive(null);
    abort.current?.abort();
    abort.current = null;
    audio.current?.pause();
    audio.current = null;
    if (url.current) URL.revokeObjectURL(url.current);
    url.current = null;
    setAudioState({ playing: false, busy: false, error: '' });
  }, []);
  useEffect(() => {
    stop();
  }, [locale, stop]);
  useEffect(() => {
    stop();
    cache.current.clear();
    requests.current.clear();
  }, [session.snapshot?.revision, query.data?.revision, location.pathname, location.search, stop]);
  useEffect(() => {
    if (session.paused || session.busy) stop();
  }, [session.paused, session.busy, stop]);
  useEffect(() => {
    const hidden = () => {
      if (document.hidden) stop();
    };
    document.addEventListener('visibilitychange', hidden);
    return () => {
      document.removeEventListener('visibilitychange', hidden);
      // eslint-disable-next-line react-hooks/exhaustive-deps -- generation counter intentionally invalidates latest async work
      epoch.current++;
      abort.current?.abort();
      audio.current?.pause();
      if (url.current) URL.revokeObjectURL(url.current);
    };
  }, [stop]);
  useEffect(() => {
    if (audio.current) {
      audio.current.volume = volume;
      audio.current.playbackRate = speed;
    }
  }, [volume, speed]);
  const play = async (token?: string, sample = false) => {
    if (!query.data?.speech_enabled || !query.data.available || session.paused || !session.snapshot)
      return;
    if ((audioState.busy || audioState.playing) && active === (sample ? 'hello' : token)) {
      stop();
      return;
    }
    stop();
    setActive(sample ? 'hello' : token || null);
    const turn = epoch.current,
      identity = [
        token || 'hello',
        session.snapshot.revision,
        query.data.revision,
        sample ? getUiLocale() : 'reply',
      ].join(':');
    setAudioState({ playing: false, busy: true, error: '' });
    abort.current = new AbortController();
    try {
      let result = cache.current.get(identity);
      if (!result) {
        let id = requests.current.get(identity);
        if (!id) {
          id = crypto.randomUUID();
          requests.current.set(identity, id);
        }
        result = await session.client.speak(
          session.kidId,
          {
            request_id: id,
            memory_revision: session.snapshot.revision,
            voice_revision: query.data.revision,
            language: getUiLocale(),
            ...(sample ? { sample: 'hello' as const } : { speech_token: token }),
          },
          abort.current.signal,
          session.audience === 'parent',
        );
        if (epoch.current !== turn) return;
        cache.current.set(identity, result);
        while (
          cache.current.size > 12 ||
          Array.from(cache.current.values()).reduce(
            (sum, item) => sum + item.audio_base64.length,
            0,
          ) > 4_000_000
        ) {
          const oldest = cache.current.keys().next().value;
          if (oldest === undefined) break;
          cache.current.delete(oldest);
        }
        while (requests.current.size > 64) {
          const oldest = requests.current.keys().next().value;
          if (oldest === undefined) break;
          requests.current.delete(oldest);
        }
      }
      const bytes = Uint8Array.from(atob(result.audio_base64), (c) => c.charCodeAt(0));
      url.current = URL.createObjectURL(new Blob([bytes], { type: result.mime_type }));
      const player = new Audio(url.current);
      audio.current = player;
      player.volume = volume;
      player.playbackRate = speed;
      player.onended = () => {
        if (epoch.current === turn) stop();
      };
      player.onerror = () => {
        if (epoch.current === turn) {
          stop();
          setAudioState({
            playing: false,
            busy: false,
            error: t('The audio could not play. Try again.'),
          });
        }
      };
      await player.play();
      if (epoch.current === turn) setAudioState({ playing: true, busy: false, error: '' });
    } catch (error) {
      if (epoch.current !== turn) return;
      setAudioState({ playing: false, busy: false, error: voiceError(error) });
      await query.refetch();
    }
  };
  return (
    <AudioSessionContext.Provider
      value={{
        settings: query.data || null,
        loading: query.isPending,
        ...audioState,
        active,
        volume,
        speed,
        setVolume,
        setSpeed,
        play,
        stop,
        refresh: async () => {
          await query.refetch();
        },
      }}
    >
      {children}
    </AudioSessionContext.Provider>
  );
}
export function CompanionReadReply({ message }: { message: BuddyMessage }) {
  useUiLocale();
  const voice = useBuddyAudio(),
    session = useCompanion();
  if (!message.speech_token || !voice.settings?.available || !voice.settings.speech_enabled)
    return null;
  const selected = voice.active === message.speech_token;
  return (
    <div className="mt-3">
      <button
        type="button"
        className="buddy-remember inline-flex items-center gap-2"
        disabled={!voice.settings?.speech_enabled || !voice.settings.available || session.paused}
        onClick={() => void voice.play(message.speech_token)}
      >
        {voice.busy || voice.playing ? <Square size={13} /> : <Volume2 size={14} />}{' '}
        {selected && voice.busy
          ? t('Stop preparing')
          : selected && voice.playing
            ? t('Stop reading')
            : t('Read aloud')}
      </button>
      {voice.error && (
        <p role="alert" className="mt-2 text-xs text-danger-600">
          {t(voice.error)}
        </p>
      )}
    </div>
  );
}
export function CompanionVoicePreferences() {
  useUiLocale();
  const session = useCompanion(),
    voice = useBuddyAudio(),
    cache = useQueryClient(),
    [draft, setDraft] = useState<Pick<
      BuddyVoiceSettings,
      'speech_enabled' | 'transcription_enabled' | 'voice'
    > | null>(null),
    [saving, setSaving] = useState(false),
    [error, setError] = useState(''),
    [saved, setSaved] = useState(false);
  const settings = voice.settings;
  useEffect(() => {
    if (settings)
      setDraft({
        speech_enabled: settings.speech_enabled,
        transcription_enabled: settings.transcription_enabled,
        voice: settings.voice,
      });
  }, [settings]);
  if (!settings || !draft) return null;
  const dirty =
    draft.voice !== settings.voice ||
    draft.speech_enabled !== settings.speech_enabled ||
    draft.transcription_enabled !== settings.transcription_enabled;
  return (
    <section className="card my-6 p-6">
      <h2 className="text-lg font-bold">{t("Your buddy's voice")}</h2>
      <p className="mt-2 text-sm leading-relaxed text-slate2">
        {' '}
        {t(
          'ElevenLabs processes selected reply text and recordings using its account data settings. Airbotix does not save recordings. Your child chooses when to transcribe and reviews the words before sending.',
        )}{' '}
      </p>
      <div className="mt-4 space-y-4">
        <label className="flex gap-3 text-sm">
          <input
            type="checkbox"
            checked={draft.speech_enabled}
            onChange={(event) => setDraft({ ...draft, speech_enabled: event.target.checked })}
          />{' '}
          {t('Allow spoken replies')}{' '}
        </label>
        <label className="flex gap-3 text-sm">
          <input
            type="checkbox"
            checked={draft.transcription_enabled}
            onChange={(event) =>
              setDraft({ ...draft, transcription_enabled: event.target.checked })
            }
          />{' '}
          {t('Allow short recordings and transcription')}{' '}
        </label>
        <label className="block text-sm font-semibold">
          {' '}
          {t('Voice')}{' '}
          <select
            aria-label={t('Buddy voice')}
            className="input mt-2 w-full"
            value={draft.voice}
            onChange={(event) =>
              setDraft({ ...draft, voice: event.target.value as BuddyVoiceSettings['voice'] })
            }
          >
            {settings.voices.map((option) => (
              <option key={option.value} value={option.value}>
                {t(option.label)} - {t(option.description)}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="mt-5 flex flex-wrap gap-3">
        <button
          className="btn-pill-primary"
          disabled={saving || !dirty}
          onClick={async () => {
            setSaving(true);
            setError('');
            voice.stop();
            try {
              const next = await session.client.saveVoice(session.kidId, settings.revision, draft);
              cache.setQueryData(['companion-voice', session.audience, session.kidId], next);
              setSaved(true);
            } catch (error) {
              setError(voiceError(error));
              await voice.refresh();
            } finally {
              setSaving(false);
            }
          }}
        >
          {saving ? t('Saving...') : t('Save voice settings')}
        </button>
        <button
          className="btn-pill-ghost"
          disabled={
            !settings.available ||
            !settings.speech_enabled ||
            saving ||
            draft.voice !== settings.voice
          }
          onClick={() => void voice.play(undefined, true)}
        >
          {voice.busy || voice.playing ? t('Stop preview') : t('Listen to this voice')}
        </button>
      </div>
      {dirty ? (
        <p className="mt-3 text-xs text-slate2">{t('Save your changes when you are ready.')}</p>
      ) : (
        saved && (
          <p role="status" className="mt-3 text-xs text-slate2">
            {' '}
            {t('Voice settings saved.')}{' '}
          </p>
        )
      )}
      {!settings.available && (
        <p className="mt-3 text-sm text-slate2">
          {' '}
          {t('Voice is currently unavailable. Your saved choice will be kept.')}{' '}
        </p>
      )}
      {(error || voice.error) && (
        <p role="alert" className="mt-3 text-sm text-danger-600">
          {t(error || voice.error)}
        </p>
      )}
    </section>
  );
}
export function CompanionVoiceOptions({ onClose }: { onClose: () => void }) {
  useUiLocale();
  const voice = useBuddyAudio(),
    dialog = useRef<HTMLDialogElement>(null);
  const stopVoice = voice.stop;
  useEffect(() => {
    stopVoice();
    const prior = document.activeElement;
    dialog.current?.showModal();
    return () => {
      stopVoice();
      if (prior instanceof HTMLElement && prior.isConnected) prior.focus();
    };
  }, [stopVoice]);
  return (
    <dialog
      ref={dialog}
      className="companion-dialog"
      onCancel={onClose}
      aria-label={t('Voice and recording')}
    >
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">{t('Voice and recording')}</h2>
        <button className="btn-pill-ghost" onClick={onClose} aria-label={t('Close voice settings')}>
          x
        </button>
      </div>
      <p className="mt-4 text-sm">
        {voice.settings?.speech_enabled
          ? t('You choose when your buddy reads aloud.')
          : t('Ask a grown-up to turn spoken replies on.')}
      </p>
      <p className="mt-2 text-sm">
        {voice.settings?.transcription_enabled
          ? t('Record up to 20 seconds, then check your words.')
          : t('Short recordings are switched off.')}
      </p>
      <label className="mt-5 block text-sm">
        {' '}
        {t('Volume')}{' '}
        <input
          className="mt-2 w-full"
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={voice.volume}
          onChange={(e) => voice.setVolume(Number(e.target.value))}
        />
      </label>
      <label className="mt-4 block text-sm">
        {' '}
        {t('Reading speed')}{' '}
        <select
          className="input mt-2 w-full"
          value={voice.speed}
          onChange={(e) => voice.setSpeed(Number(e.target.value))}
        >
          <option value="0.85">{t('A little slower')}</option>
          <option value="1">{t('Comfortable')}</option>
          <option value="1.1">{t('A little faster')}</option>
        </select>
      </label>
      <button
        className="btn-pill-primary mt-5"
        disabled={!voice.settings?.speech_enabled || !voice.settings.available}
        onClick={() => void voice.play(undefined, true)}
      >
        {voice.busy || voice.playing ? t('Stop voice') : t('Say hello')}
      </button>
      {voice.error && (
        <p role="alert" className="mt-3 text-sm text-danger-600">
          {t(voice.error)}
        </p>
      )}
    </dialog>
  );
}
export function CompanionRecordButton() {
  useUiLocale();
  const voice = useBuddyAudio(),
    session = useCompanion(),
    [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  if (!voice.settings?.available || !voice.settings.transcription_enabled) return null;
  return (
    <>
      <button
        type="button"
        className="companion-record-button"
        aria-label={t('Record a voice message')}
        disabled={
          session.paused ||
          session.busy ||
          !voice.settings?.transcription_enabled ||
          !voice.settings.available
        }
        onClick={() => {
          voice.stop();
          setOpen(true);
        }}
      >
        <Mic size={20} />
      </button>
      {open && <RecordingDialog onClose={close} />}
    </>
  );
}
function RecordingDialog({ onClose }: { onClose: () => void }) {
  useUiLocale();
  const session = useCompanion(),
    voice = useBuddyAudio(),
    dialog = useRef<HTMLDialogElement>(null),
    recorder = useRef<MediaRecorder | null>(null),
    previewAudio = useRef<HTMLAudioElement | null>(null),
    stream = useRef<MediaStream | null>(null),
    timer = useRef<ReturnType<typeof setInterval> | null>(null),
    epoch = useRef(0),
    request = useRef<string | null>(null),
    abort = useRef<AbortController | null>(null);
  const [status, setStatus] = useState('idle'),
    [seconds, setSeconds] = useState(0),
    [blob, setBlob] = useState<Blob | null>(null),
    [preview, setPreview] = useState(''),
    [words, setWords] = useState(''),
    [error, setError] = useState('');
  const release = useCallback(() => {
    if (timer.current) clearInterval(timer.current);
    timer.current = null;
    if (recorder.current?.state === 'recording') recorder.current.stop();
    stream.current?.getTracks().forEach((track) => track.stop());
    stream.current = null;
  }, []);
  useEffect(() => {
    const localAudio = previewAudio;
    const prior = document.activeElement;
    dialog.current?.showModal();
    const hidden = () => {
      if (document.hidden) onClose();
    };
    document.addEventListener('visibilitychange', hidden);
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps -- generation counter intentionally invalidates latest async work
      epoch.current++;
      abort.current?.abort();
      localAudio.current?.pause();
      release();
      document.removeEventListener('visibilitychange', hidden);
      if (prior instanceof HTMLElement && prior.isConnected) prior.focus();
    };
  }, [onClose, release]);
  useEffect(() => {
    if (!blob) {
      setPreview('');
      return;
    }
    const url = URL.createObjectURL(blob);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [blob]);
  useEffect(() => {
    if (session.paused || !voice.settings?.transcription_enabled || !voice.settings.available)
      onClose();
  }, [session.paused, voice.settings?.transcription_enabled, voice.settings?.available, onClose]);
  const start = async () => {
    setError('');
    setBlob(null);
    setWords('');
    request.current = null;
    setStatus('requesting');
    const turn = ++epoch.current;
    try {
      if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined')
        throw Error(t('Recording is not available in this browser.'));
      const media = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (epoch.current !== turn) {
        media.getTracks().forEach((track) => track.stop());
        return;
      }
      stream.current = media;
      const chunks: BlobPart[] = [],
        r = new MediaRecorder(media);
      recorder.current = r;
      r.ondataavailable = (event) => {
        if (event.data.size) chunks.push(event.data);
      };
      r.onstop = () => {
        if (epoch.current !== turn) return;
        setBlob(new Blob(chunks, { type: r.mimeType }));
        setStatus('review');
      };
      r.start();
      setStatus('recording');
      setSeconds(0);
      let elapsed = 0;
      timer.current = setInterval(() => {
        elapsed++;
        setSeconds(elapsed);
        if (elapsed >= 20) release();
      }, 1000);
    } catch (error) {
      if (epoch.current === turn) {
        release();
        setStatus('idle');
        setError(
          error instanceof Error && error.name === 'NotAllowedError'
            ? 'Microphone permission was not allowed.'
            : error instanceof Error && error.name === 'NotFoundError'
              ? 'No microphone was found.'
              : 'Could not start recording.',
        );
      }
    }
  };
  const transcribe = async () => {
    if (!blob || !session.snapshot || !voice.settings) return;
    const turn = epoch.current;
    setStatus('transcribing');
    setError('');
    abort.current = new AbortController();
    try {
      const wav = await recordingToWav(blob);
      if (turn !== epoch.current) return;
      request.current ||= crypto.randomUUID();
      const result = await session.client.transcribe(
        session.kidId,
        {
          request_id: request.current,
          memory_revision: session.snapshot.revision,
          voice_revision: voice.settings.revision,
          audio_base64: wav,
          confirmed: true,
        },
        abort.current.signal,
      );
      if (turn !== epoch.current) return;
      setWords(result.text);
      setStatus('text');
    } catch (error) {
      if (turn !== epoch.current) return;
      setStatus('review');
      setError(
        error instanceof ApiError
          ? voiceError(error)
          : error instanceof Error
            ? error.message
            : t('Could not read that recording.'),
      );
    }
  };
  return (
    <dialog
      ref={dialog}
      className="companion-dialog"
      aria-labelledby="recording-heading"
      onCancel={onClose}
    >
      <div className="flex items-center justify-between">
        <h2 id="recording-heading" className="text-xl font-bold">
          {' '}
          {t('Tell me in your own voice')}{' '}
        </h2>
        <button className="btn-pill-ghost" onClick={onClose} aria-label={t('Discard recording')}>
          x
        </button>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-slate2">
        {' '}
        {t(
          'Up to 20 seconds. Listen first. Only choose Transcribe when you are ready to send this recording to ElevenLabs.',
        )}{' '}
      </p>
      {status === 'recording' && (
        <p role="status" className="my-5 text-lg font-bold">
          {' '}
          {t('Recording')} {seconds} {t('/ 20 seconds')}
        </p>
      )}
      {status === 'requesting' && (
        <p role="status" className="my-5 text-sm">
          {' '}
          {t('Waiting for microphone permission...')}{' '}
        </p>
      )}
      {preview && <audio ref={previewAudio} className="my-5 w-full" src={preview} controls />}
      {status === 'text' && (
        <label className="mt-4 block text-sm font-semibold">
          {' '}
          {t('Check your words')}{' '}
          <textarea
            aria-label={t('Transcribed words')}
            className="input mt-2 w-full"
            rows={4}
            maxLength={500}
            value={words}
            onChange={(event) => setWords(event.target.value)}
          />
        </label>
      )}
      {error && (
        <p role="alert" className="mt-3 text-sm text-danger-600">
          {t(error)}
        </p>
      )}
      <div className="mt-5 flex flex-wrap gap-3">
        <button className="btn-pill-ghost" onClick={onClose}>
          {' '}
          {t('Discard')}{' '}
        </button>
        {status === 'idle' && (
          <button className="btn-pill-primary" onClick={() => void start()}>
            {' '}
            {t('Start recording')}{' '}
          </button>
        )}
        {status === 'recording' && (
          <button className="btn-pill-primary" onClick={release}>
            {' '}
            {t('Stop recording')}{' '}
          </button>
        )}
        {status === 'review' && (
          <button className="btn-pill-primary" onClick={() => void transcribe()}>
            {' '}
            {t('Transcribe recording')}{' '}
          </button>
        )}
        {status === 'transcribing' && (
          <span role="status" className="text-sm">
            {' '}
            {t('Writing down your words...')}{' '}
          </span>
        )}
        {status === 'text' && (
          <button
            className="btn-pill-primary"
            disabled={!words.trim()}
            onClick={() => {
              session.setDraft(words.trim());
              onClose();
            }}
          >
            {' '}
            {t('Use these words')}{' '}
          </button>
        )}
      </div>
      {['idle', 'review', 'text'].includes(status) && (
        <label className="mt-5 block text-xs text-slate2">
          {' '}
          {t('Or choose a short audio file')}{' '}
          <input
            aria-label={t('Choose an audio file')}
            type="file"
            accept="audio/*"
            className="mt-2 block max-w-full"
            onChange={(event) => {
              const selected = event.target.files?.[0];
              if (selected) {
                setBlob(selected);
                setWords('');
                request.current = null;
                setStatus('review');
                setError('');
              }
            }}
          />
        </label>
      )}
    </dialog>
  );
}
